import React from 'react';
import { Alert, Box, Button, ButtonBase } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { getActivity } from './api/botOpsService';
import { usePrefs } from './hooks/usePrefs';
import { useQuery } from './hooks/useQuery';
import { Company, Snapshot } from './lib/types';
import { digest, digestTotal } from './lib/activity';
import { fmtIct } from './lib/ict';
import { RADIUS } from './lib/brand';

const A = '/dashboard/bot-ops/activity';

/**
 * "Since you last looked": what changed since the stored last look (or, on a first
 * visit, in the 24 h before the snapshot, and it says so). Each counter opens the
 * Activity page already filtered. It loads quietly after the page and stays out of
 * the way: no data, an error, or the digest switched off in preferences all show nothing.
 */
export function DigestStrip({ snapshot, company, canWrite }: { snapshot: Snapshot; company: Company; canWrite: boolean }) {
  const activity = useQuery(getActivity, []);
  const { prefs, markSeen: saveSeen } = usePrefs();
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  if (!activity.data || !prefs || prefs.notify.digest === false) return null;
  const d = digest(snapshot, activity.data.feed, prefs.last_seen_at, company);
  if (!d.firstVisit && digestTotal(d) === 0) return null;

  const q = (extra: string) => `${A}?scope=current&company=${company}&since=${encodeURIComponent(d.since)}${extra}`;
  const items: { label: string; n: number; to: string }[] = [
    { label: 'new recommendations', n: d.newRecommendations, to: q('&type=recommendation') },
    { label: 'verdict changes', n: d.verdictChanges, to: q('&type=verdict') },
    { label: 'bot replies', n: d.botReplies, to: q('&type=reply') },
    { label: 'documents updated', n: d.documentsUpdated, to: q('&type=document') },
    { label: 'implementations done', n: d.implementationsDone, to: `/dashboard/bot-ops?${company === 'quill' ? 'company=quill&' : ''}decision=approved&impl=done` },
    ...(prefs.notify.idle_alerts === false ? [] : [{ label: 'idle alerts', n: d.idleAlerts, to: `${A}?scope=current&company=${company}` }]),
  ];

  const markSeen = async () => {
    setBusy(true);
    setErr(null);
    try { if (!(await saveSeen())) setErr('Could not save. Try again.'); } finally { setBusy(false); }
  };

  return (
    <Alert
      icon={false}
      severity="info"
      data-testid="digest"
      sx={{ mb: 2, alignItems: 'center', '& .MuiAlert-message': { width: '100%' } }}
      action={canWrite ? <Button color="inherit" size="small" disabled={busy} onClick={markSeen}>Mark all as seen</Button> : undefined}
    >
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <Box component="span" sx={{ fontWeight: 700, fontSize: 13 }}>Since you last looked</Box>
        <Box component="span" sx={{ fontSize: 12 }}>{d.firstVisit ? `First visit: showing the 24 h before this snapshot (since ${fmtIct(d.since)}).` : `since ${fmtIct(d.since)}`}</Box>
        {items.map((i) => (
          <ButtonBase key={i.label} component={RouterLink} to={i.to} aria-label={`${i.n} ${i.label}`} sx={{ fontSize: 12.5, px: 1, py: 0.2, borderRadius: RADIUS.pill, border: '1px solid', borderColor: i.n ? 'primary.main' : 'divider', color: i.n ? 'text.primary' : 'text.secondary', fontWeight: i.n ? 700 : 400 }}>
            {i.n} {i.label}
          </ButtonBase>
        ))}
        {err && <Box component="span" role="alert" sx={{ color: 'error.main', fontSize: 12 }}>{err}</Box>}
      </Box>
    </Alert>
  );
}
